<template>
  <div class="pdf-reader">
    <header class="pdf-header">
      <button class="icon-button" type="button" aria-label="返回" @click="goBack"><nricon name="arrow-left" /></button>
      <div class="pdf-title"><strong>{{ displayTitle }}</strong><span>PDF 文档</span></div>
      <div class="header-tools">
        <button class="icon-button" type="button" :aria-expanded="outlineOpen ? 'true' : 'false'" aria-label="文档目录" title="文档目录" @click="outlineOpen = !outlineOpen"><nricon name="notebook-1" /></button>
        <button v-if="$store.state.token" class="icon-button" type="button" aria-label="收藏此页" title="收藏此页" @click="addBookmark"><nricon name="star-off" /></button>
      </div>
    </header>

    <div class="pdf-workspace" :class="{ 'outline-is-open': outlineOpen }">
      <aside v-if="outlineOpen" class="outline-panel" aria-label="PDF 文档目录">
        <div class="outline-heading"><strong>文档目录</strong><button type="button" aria-label="关闭目录" @click="outlineOpen = false"><nricon name="close" :size="16" /></button></div>
        <nav v-if="outline.length" class="outline-list">
          <button v-for="(item, index) in outline" :key="index" type="button" :style="{ paddingLeft: (12 + item.level * 14) + 'px' }" @click="goToPage(item.page)">{{ item.title }}</button>
        </nav>
        <div v-else-if="outlineLoading" class="outline-empty">正在读取目录…</div>
        <div v-else-if="totalPages" ref="thumbnailScroller" class="thumbnail-list" @scroll.passive="onThumbnailScroll">
          <div class="thumbnail-spacer" :style="{ height: (totalPages * 154) + 'px' }">
            <button v-for="pageNo in thumbnailPages" :key="pageNo" class="thumbnail-item" :class="{ current: pageNo === currentPage }" :style="{ transform: `translateY(${(pageNo - 1) * 154}px)` }" type="button" :aria-label="`跳到第 ${pageNo} 页`" @click="goToPage(pageNo)">
              <canvas :ref="'thumbnail-' + pageNo" aria-hidden="true"></canvas><span>第 {{ pageNo }} 页</span>
            </button>
          </div>
        </div>
        <div v-if="bookmarks.length" class="saved-pages">
          <strong>我的页签</strong>
          <button v-for="item in bookmarks" :key="item.id" type="button" @click="goToPage(item.pageIndex + 1)">
            <span>第 {{ item.pageIndex + 1 }} 页</span><small>{{ item.note || '页书签' }}</small>
          </button>
        </div>
      </aside>

      <main class="pdf-main">
        <div v-if="error" class="pdf-state error-state" role="alert">
          <nricon name="warning" :size="27"/><strong>{{ errorTitle }}</strong><p>{{ error }}</p>
          <button class="btn btn--sm" type="button" @click="loadDocument">重试</button>
        </div>
        <div v-else-if="loading" class="pdf-state" role="status"><nricon name="loading" :size="26"/><span>正在打开 PDF…</span></div>
        <div v-else ref="scroller" class="pdf-scroll" @scroll.passive="onScroll" @mouseup="captureSelection">
          <div class="page-flow">
            <div class="page-spacer" :style="{ height: spacerBefore + 'px' }" aria-hidden="true"></div>
            <section v-for="pageNo in visiblePages" :key="pageNo" :ref="'page-' + pageNo" class="pdf-page" :data-page="pageNo" :style="pageStyle(pageNo)">
              <div class="pdf-page-body">
                <canvas :ref="'canvas-' + pageNo" class="pdf-canvas" :aria-label="`第 ${pageNo} 页`"></canvas>
                <div :ref="'text-' + pageNo" class="pdf-text-layer textLayer"></div>
                <button v-for="mark in marksForPage(pageNo)" :key="mark.id" class="page-mark" :style="markStyle(mark)" type="button" :title="mark.note || '页书签'" @click.stop="showBookmark(mark)">•</button>
              </div>
              <div class="page-caption">{{ pageNo }} / {{ totalPages }}</div>
            </section>
            <div class="page-spacer" :style="{ height: spacerAfter + 'px' }" aria-hidden="true"></div>
          </div>
        </div>
      </main>
    </div>

    <footer v-if="!loading && !error" class="pdf-toolbar">
      <div class="page-jump">
        <label for="pdf-page-number">页码</label>
        <input id="pdf-page-number" v-model="pageInput" inputmode="numeric" :max="totalPages" min="1" type="number" @keyup.enter="jumpFromInput" @change="jumpFromInput">
        <span>/ {{ totalPages }}</span>
      </div>
      <div class="toolbar-search">
        <input v-model.trim="searchQuery" aria-label="搜索 PDF 文本" placeholder="查找文档" @keyup.enter="searchText">
        <button type="button" :disabled="searching || !searchQuery" aria-label="查找" @click="searchText"><nricon name="search" :size="16" /></button>
        <span v-if="searchMessage" class="search-message">{{ searchMessage }}</span>
        <button v-if="searchPages.length" class="search-step" type="button" aria-label="上一个结果" @click="stepSearch(-1)"><nricon name="arrow-left" :size="14" /></button>
        <button v-if="searchPages.length" class="search-step" type="button" aria-label="下一个结果" @click="stepSearch(1)"><nricon name="arrow-right" :size="14" /></button>
      </div>
      <div class="zoom-tools" aria-label="缩放">
        <button type="button" aria-label="缩小" :disabled="zoom <= 60" @click="changeZoom(-10)"><nricon name="minus" :size="16" /></button>
        <span>{{ zoom }}%</span>
        <button type="button" aria-label="放大" :disabled="zoom >= 180" @click="changeZoom(10)"><nricon name="plus" :size="16" /></button>
        <button class="fit-button" type="button" @click="fitWidth">适合宽度</button>
      </div>
    </footer>

    <div v-if="selectionText" class="selection-popover" :style="selectionStyle">
      <span>{{ selectionText.length > 36 ? selectionText.slice(0, 36) + '…' : selectionText }}</span>
      <button type="button" @mousedown.prevent @click="addBookmark(selectionText)"><nricon name="star-off" :size="15"/> 添加批注</button>
      <button class="dismiss-selection" type="button" aria-label="关闭" @click="clearSelection"><nricon name="close" :size="14"/></button>
    </div>

    <div v-if="activeBookmark" class="bookmark-toast" role="status" @click="activeBookmark = null">
      <strong>第 {{ activeBookmark.pageIndex + 1 }} 页</strong><span>{{ activeBookmark.note || '页书签' }}</span><button type="button" aria-label="关闭"><nricon name="close" :size="14"/></button>
    </div>
  </div>
</template>

<script>
import 'pdfjs-dist/web/pdf_viewer.css'
import api from '@/api'
import { cleanTitle } from '@/utils/title'

const GAP = 28
const RENDER_WINDOW = 3
const THUMBNAIL_ROW = 154

export default {
  name: 'PdfReader',
  props: { bookId: { type: Number, required: true } },
  data() {
    return {
      book: null, pdf: null, loading: true, error: '', errorTitle: '无法打开文档',
      totalPages: 0, currentPage: 1, visiblePages: [], pageHeights: {}, pageWidths: {},
      thumbnailStart: 1, thumbnailEnd: 10,
      estimatedHeight: 1060, zoom: 100, outline: [], outlineOpen: false, outlineLoading: false,
      bookmarks: [], activeBookmark: null, selectionText: '', selectionStyle: {}, selectionPage: null, selectionOffset: null,
      searchQuery: '', searchPages: [], searchCursor: 0, searchMessage: '', searching: false,
      pageInput: '1', renderedPages: new Set(), renderingPages: new Set(), searchToken: 0,
      saveTimer: null, scrollFrame: null, initialPage: 1, initialOffset: 0, pdfjsLib: null,
      zoomAnchor: null, resizeHandler: null, loadToken: 0, renderVersion: 0,
      thumbnailFrame: null, thumbnailRendered: new Set(), thumbnailRendering: new Set()
    }
  },
  computed: {
    displayTitle() { return cleanTitle(this.book && this.book.title) || 'PDF 文档' },
    renderWidth() {
      const available = Math.max(260, window.innerWidth - (this.outlineOpen && window.innerWidth > 760 ? 318 : 30))
      return Math.max(260, Math.min(980, available)) * this.zoom / 100
    },
    spacerBefore() {
      let height = 0
      for (let page = 1; page < (this.visiblePages[0] || 1); page++) height += (this.pageHeights[page] || this.estimatedHeight) + GAP
      return height
    },
    spacerAfter() {
      let height = 0
      const start = this.visiblePages.length ? this.visiblePages[this.visiblePages.length - 1] + 1 : 1
      for (let page = start; page <= this.totalPages; page++) height += (this.pageHeights[page] || this.estimatedHeight) + GAP
      return height
    },
    thumbnailPages() {
      const pages = []
      for (let page = Math.max(1, this.thumbnailStart); page <= Math.min(this.totalPages, this.thumbnailEnd); page++) pages.push(page)
      return pages
    }
  },
  watch: {
    currentPage(value) { this.pageInput = String(value); this.scheduleProgressSave() },
    zoom(value, oldValue) {
      localStorage.setItem('pdf-reader-zoom', String(value))
      const ratio = value / (Number(oldValue) || 100)
      this.pageHeights = Object.keys(this.pageHeights).reduce((result, page) => { result[page] = this.pageHeights[page] * ratio; return result }, {})
      this.pageWidths = Object.keys(this.pageWidths).reduce((result, page) => { result[page] = this.pageWidths[page] * ratio; return result }, {})
      this.estimatedHeight *= ratio
      this.renderVersion++
      this.renderedPages.clear()
      this.$nextTick(async () => {
        await this.waitForVisiblePages()
        this.restoreZoomAnchor()
      })
    },
    outlineOpen() {
      if (this.pdf) this.zoomAnchor = this.captureZoomAnchor()
      this.$nextTick(async () => {
        this.renderVersion++
        this.renderedPages.clear()
        this.thumbnailRendered.clear()
        if (this.outlineOpen && !this.outline.length) this.syncThumbnailWindow()
        await this.$nextTick()
        await this.waitForVisiblePages()
        await this.renderVisibleThumbnails()
        this.restoreZoomAnchor()
      })
    },
    bookId() { this.saveProgress(); clearTimeout(this.saveTimer); this.resetDocument(); this.initialPage = Math.max(1, Number(this.$route.query.page) || 1); this.initialOffset = 0; this.loadDocument() }
  },
  created() {
    const storedZoom = Number(localStorage.getItem('pdf-reader-zoom'))
    this.zoom = storedZoom >= 60 && storedZoom <= 180 ? storedZoom : 100
    this.initialPage = Math.max(1, Number(this.$route.query.page) || 1)
    this.resizeHandler = () => this.reflowDocument()
    this.loadDocument()
  },
  mounted() { window.addEventListener('resize', this.resizeHandler) },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
    this.loadToken++
    clearTimeout(this.saveTimer)
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame)
    this.saveProgress()
    if (this.pdf && this.pdf.destroy) this.pdf.destroy()
    this.pdf = null
  },
  methods: {
    resetDocument() {
      this.loadToken++
      this.renderVersion++
      if (this.pdf && this.pdf.destroy) this.pdf.destroy()
      this.pdf = null; this.book = null; this.loading = true; this.error = ''; this.totalPages = 0; this.currentPage = 1
      this.visiblePages = []; this.pageHeights = {}; this.pageWidths = {}; this.bookmarks = []; this.outline = []
      this.renderedPages.clear(); this.renderingPages.clear(); this.zoomAnchor = null
      this.thumbnailStart = 1; this.thumbnailEnd = 10; this.thumbnailRendered.clear(); this.thumbnailRendering.clear()
    },
    async loadDocument() {
      const loadToken = ++this.loadToken
      this.loading = true; this.error = ''; this.errorTitle = '无法打开文档'
      try {
        const { data: book } = await api.book.getBookDetail(this.bookId)
        if (loadToken !== this.loadToken) return
        if (book.mediaType !== 'PDF') throw new Error('这项内容不是 PDF 文档。')
        this.book = book
        if (this.$store.state.token) {
          const { data } = await api.progress.getReadingProgress(this.bookId).catch(() => ({ data: null }))
          if (loadToken !== this.loadToken) return
          if (this.$route.query.page) this.initialPage = Math.max(1, Number(this.$route.query.page) || 1)
          else if (data && data.mediaType === 'PDF' && data.pageIndex != null) {
            this.initialPage = data.pageIndex + 1
            this.initialOffset = Math.max(0, Math.min(1, Number(data.pageOffset) || 0))
          }
          this.bookmarks = await api.bookmark.list(this.bookId).then(result => result.data || []).catch(() => [])
          if (loadToken !== this.loadToken) return
        } else if (!this.$route.query.page) {
          try {
            const saved = JSON.parse(localStorage.getItem(`nr-pdf-progress:${this.bookId}`) || 'null')
            if (saved && Number(saved.pageIndex) >= 0) {
              this.initialPage = Number(saved.pageIndex) + 1
              this.initialOffset = Math.max(0, Math.min(1, Number(saved.pageOffset) || 0))
            }
          } catch (error) { localStorage.removeItem(`nr-pdf-progress:${this.bookId}`) }
        }
        const token = localStorage.getItem('token')
        const pdfjsLib = await this.loadPdfJs()
        this.pdf = await pdfjsLib.getDocument({
          url: api.book.getPdfResourceUrl(this.bookId),
          httpHeaders: token ? { Authorization: `Bearer ${token}` } : {},
          isEvalSupported: false,
          rangeChunkSize: 65536,
          disableAutoFetch: true,
          disableStream: true,
          enableXfa: false
        }).promise
        if (loadToken !== this.loadToken) { this.pdf.destroy(); return }
        this.totalPages = this.pdf.numPages
        this.currentPage = Math.min(this.initialPage, this.totalPages)
        this.estimatedHeight = Math.max(640, this.renderWidth * 1.42)
        this.visiblePages = this.makeWindow(this.currentPage)
        this.loading = false
        this.outlineLoading = true
        this.loadOutline(loadToken, this.pdf)
        this.$nextTick(() => this.goToPage(this.currentPage, false, this.initialOffset))
      } catch (error) {
        if (loadToken !== this.loadToken) return
        this.loading = false
        const status = error.response && error.response.status
        if (status === 401 || status === 403) {
          this.errorTitle = '没有访问权限'
          this.error = '此 PDF 已设为私有，或你的访问权限已失效。'
        } else if (error.name === 'PasswordException') {
          this.errorTitle = '暂不支持加密文档'
          this.error = '这个 PDF 受密码保护，当前版本无法打开。'
        } else if (error.message === '这项内容不是 PDF 文档。') {
          this.error = error.message
        } else {
          this.error = '文件可能已损坏、格式不受支持，或网络连接中断。请重试；如果仍然失败，请检查原始文件。'
        }
      }
    },
    async loadPdfJs() {
      if (this.pdfjsLib) return this.pdfjsLib
      if (!Promise.withResolvers) {
        Promise.withResolvers = function () {
          let resolve; let reject
          const promise = new Promise((yes, no) => { resolve = yes; reject = no })
          return { promise, resolve, reject }
        }
      }
      this.pdfjsLib = await import(/* webpackIgnore: true */ `${process.env.BASE_URL || '/'}pdfjs.min.js`)
      this.pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL || '/'}pdf.worker.min.js`
      return this.pdfjsLib
    },
    makeWindow(center) {
      const first = Math.max(1, center - RENDER_WINDOW)
      const last = Math.min(this.totalPages, center + RENDER_WINDOW)
      const pages = []
      for (let page = first; page <= last; page++) pages.push(page)
      return pages
    },
    pageStyle(pageNo) {
      const height = this.pageHeights[pageNo] || this.estimatedHeight
      return { width: this.renderWidth + 'px', minHeight: height + 'px' }
    },
    async renderVisiblePages() {
      if (!this.pdf || !this.visiblePages.length) return
      const keep = new Set(this.visiblePages)
      this.renderedPages.forEach(page => { if (!keep.has(page)) this.renderedPages.delete(page) })
      await Promise.all(this.visiblePages.filter(pageNo => !this.renderedPages.has(pageNo) && !this.renderingPages.has(pageNo)).map(pageNo => this.renderPage(pageNo)))
    },
    async waitForVisiblePages() {
      await this.renderVisiblePages()
      while (this.visiblePages.some(pageNo => this.renderingPages.has(pageNo))) {
        await new Promise(resolve => setTimeout(resolve, 16))
      }
    },
    async renderPage(pageNo) {
      if (!this.pdf || this.renderingPages.has(pageNo)) return
      this.renderingPages.add(pageNo)
      const renderVersion = this.renderVersion
      const renderWidth = this.renderWidth
      const pdfDocument = this.pdf
      try {
        const page = await pdfDocument.getPage(pageNo)
        await this.$nextTick()
        if (renderVersion !== this.renderVersion || pdfDocument !== this.pdf) return
        const canvasRef = this.$refs[`canvas-${pageNo}`]
        const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef
        const textRef = this.$refs[`text-${pageNo}`]
        const textLayer = Array.isArray(textRef) ? textRef[0] : textRef
        if (!canvas || !textLayer || !this.visiblePages.includes(pageNo)) return
        const baseViewport = page.getViewport({ scale: 1 })
        const scale = renderWidth / baseViewport.width
        const viewport = page.getViewport({ scale })
        const dpr = Math.min(2, window.devicePixelRatio || 1)
        canvas.width = Math.floor(viewport.width * dpr)
        canvas.height = Math.floor(viewport.height * dpr)
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`
        textLayer.style.width = `${viewport.width}px`
        textLayer.style.height = `${viewport.height}px`
        textLayer.innerHTML = ''
        this.pageWidths = { ...this.pageWidths, [pageNo]: viewport.width }
        this.pageHeights = { ...this.pageHeights, [pageNo]: viewport.height }
        const context = canvas.getContext('2d', { alpha: false })
        await page.render({ canvasContext: context, viewport, transform: dpr === 1 ? null : [dpr, 0, 0, dpr, 0, 0] }).promise
        if (renderVersion !== this.renderVersion || Math.abs(renderWidth - this.renderWidth) > 1 || !this.visiblePages.includes(pageNo)) return
        const textContent = await page.getTextContent()
        const textLayerTask = new this.pdfjsLib.TextLayer({ textContentSource: textContent, container: textLayer, viewport })
        await textLayerTask.render()
        if (renderVersion !== this.renderVersion || Math.abs(renderWidth - this.renderWidth) > 1) return
        this.renderedPages.add(pageNo)
        this.updateCurrentFromNodes()
      } catch (error) {
        if (this.visiblePages.includes(pageNo)) console.warn('PDF 页面渲染失败', pageNo, error)
      } finally {
        this.renderingPages.delete(pageNo)
        const staleRender = renderVersion !== this.renderVersion || Math.abs(renderWidth - this.renderWidth) > 1
        if (staleRender && this.pdf && this.visiblePages.includes(pageNo) && !this.renderedPages.has(pageNo)) this.$nextTick(() => this.renderVisiblePages())
      }
    },
    async loadOutline(loadToken, pdfDocument) {
      try {
        const raw = await pdfDocument.getOutline()
        if (loadToken !== this.loadToken) return
        const flattened = []
        const visit = async (items, level) => {
          for (const item of items || []) {
            let page = 1
            try {
              const destination = typeof item.dest === 'string' ? await pdfDocument.getDestination(item.dest) : item.dest
              if (destination && destination[0]) page = (await pdfDocument.getPageIndex(destination[0])) + 1
            } catch (error) { /* malformed outline targets do not block the rest */ }
            flattened.push({ title: item.title || '未命名', page, level })
            if (item.items && item.items.length) await visit(item.items, level + 1)
          }
        }
        await visit(raw, 0)
        this.outline = flattened
        if (flattened.length) this.thumbnailRendered.clear()
      } catch (error) {
        if (loadToken === this.loadToken) this.outline = []
      } finally {
        if (loadToken === this.loadToken) {
          this.outlineLoading = false
          if (!this.outline.length && this.outlineOpen) this.$nextTick(this.renderVisibleThumbnails)
        }
      }
    },
    syncThumbnailWindow() {
      const scroller = this.$refs.thumbnailScroller
      if (!scroller) return
      scroller.scrollTop = Math.max(0, (this.currentPage - 1) * THUMBNAIL_ROW - scroller.clientHeight / 2)
      const first = Math.floor(scroller.scrollTop / THUMBNAIL_ROW) + 1
      const visible = Math.ceil(scroller.clientHeight / THUMBNAIL_ROW)
      this.thumbnailStart = Math.max(1, first - 2)
      this.thumbnailEnd = Math.min(this.totalPages, first + visible + 2)
    },
    onThumbnailScroll() {
      if (this.thumbnailFrame) return
      this.thumbnailFrame = requestAnimationFrame(() => {
        this.thumbnailFrame = null
        const scroller = this.$refs.thumbnailScroller
        if (!scroller) return
        const first = Math.floor(scroller.scrollTop / THUMBNAIL_ROW) + 1
        const visible = Math.ceil(scroller.clientHeight / THUMBNAIL_ROW)
        this.thumbnailStart = Math.max(1, first - 2)
        this.thumbnailEnd = Math.min(this.totalPages, first + visible + 2)
        const keep = new Set(this.thumbnailPages)
        this.thumbnailRendered.forEach(page => { if (!keep.has(page)) this.thumbnailRendered.delete(page) })
        this.$nextTick(this.renderVisibleThumbnails)
      })
    },
    async renderVisibleThumbnails() {
      if (!this.pdf || !this.outlineOpen || this.outline.length || !this.thumbnailPages.length) return
      await Promise.all(this.thumbnailPages.filter(page => !this.thumbnailRendered.has(page) && !this.thumbnailRendering.has(page)).map(page => this.renderThumbnail(page)))
    },
    async renderThumbnail(pageNo) {
      if (!this.pdf || this.thumbnailRendering.has(pageNo)) return
      this.thumbnailRendering.add(pageNo)
      const pdfDocument = this.pdf
      const loadToken = this.loadToken
      try {
        const page = await pdfDocument.getPage(pageNo)
        await this.$nextTick()
        if (pdfDocument !== this.pdf || loadToken !== this.loadToken) return
        const ref = this.$refs[`thumbnail-${pageNo}`]
        const canvas = Array.isArray(ref) ? ref[0] : ref
        if (!canvas || !this.thumbnailPages.includes(pageNo) || this.outline.length) return
        const baseViewport = page.getViewport({ scale: 1 })
        const viewport = page.getViewport({ scale: Math.min(1, 104 / baseViewport.width) })
        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`
        await page.render({ canvasContext: canvas.getContext('2d', { alpha: false }), viewport }).promise
        this.thumbnailRendered.add(pageNo)
      } catch (error) {
        if (this.thumbnailPages.includes(pageNo)) console.warn('PDF 缩略页渲染失败', pageNo, error)
      } finally {
        this.thumbnailRendering.delete(pageNo)
      }
    },
    onScroll() {
      if (this.scrollFrame) return
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null
        this.updateCurrentFromNodes()
        const nextWindow = this.makeWindow(this.currentPage)
        if (nextWindow.join(',') !== this.visiblePages.join(',')) {
          this.visiblePages = nextWindow
          this.$nextTick(this.renderVisiblePages)
        }
        this.scheduleProgressSave()
      })
    },
    updateCurrentFromNodes() {
      const scroller = this.$refs.scroller
      if (!scroller) return
      const targetY = scroller.getBoundingClientRect().top + Math.min(190, scroller.clientHeight * 0.34)
      let nearest = null; let distance = Infinity
      this.visiblePages.forEach(pageNo => {
        const ref = this.$refs[`page-${pageNo}`]
        const node = Array.isArray(ref) ? ref[0] : ref
        if (!node) return
        const rect = node.getBoundingClientRect()
        const d = targetY < rect.top ? rect.top - targetY : targetY > rect.bottom ? targetY - rect.bottom : 0
        if (d < distance) { distance = d; nearest = pageNo }
      })
      if (nearest && nearest !== this.currentPage) this.currentPage = nearest
    },
    async goToPage(page, smooth = true, pageOffset = 0) {
      if (!this.pdf || !this.totalPages) return
      const target = Math.max(1, Math.min(this.totalPages, Number(page) || 1))
      this.currentPage = target
      this.visiblePages = this.makeWindow(target)
      await this.$nextTick()
      await this.waitForVisiblePages()
      await this.$nextTick()
      const ref = this.$refs[`page-${target}`]
      const node = Array.isArray(ref) ? ref[0] : ref
      if (node && this.$refs.scroller) {
        const top = node.getBoundingClientRect().top - this.$refs.scroller.getBoundingClientRect().top + this.$refs.scroller.scrollTop
        const anchorY = Math.min(190, this.$refs.scroller.clientHeight * 0.34)
        const offset = Math.max(0, Math.min(1, Number(pageOffset) || 0))
        this.$refs.scroller.scrollTo({ top: Math.max(0, top + node.offsetHeight * offset - anchorY), behavior: smooth ? 'smooth' : 'auto' })
      }
    },
    jumpFromInput() { this.goToPage(this.pageInput) },
    currentPageOffset() {
      const scroller = this.$refs.scroller
      const ref = this.$refs[`page-${this.currentPage}`]
      const node = Array.isArray(ref) ? ref[0] : ref
      if (!scroller || !node || !node.offsetHeight) return 0
      const anchorY = scroller.getBoundingClientRect().top + Math.min(190, scroller.clientHeight * 0.34)
      const rect = node.getBoundingClientRect()
      return Math.max(0, Math.min(1, (anchorY - rect.top) / rect.height))
    },
    captureZoomAnchor() {
      return { page: this.currentPage, offset: this.currentPageOffset() }
    },
    restoreZoomAnchor() {
      const anchor = this.zoomAnchor
      if (!anchor || !this.pdf) return
      this.zoomAnchor = null
      this.goToPage(anchor.page, false, anchor.offset)
    },
    async reflowDocument() {
      if (!this.pdf) return
      this.zoomAnchor = this.captureZoomAnchor()
      this.renderVersion++
      this.estimatedHeight = Math.max(640, this.renderWidth * 1.42)
      this.renderedPages.clear()
      await this.$nextTick()
      await this.waitForVisiblePages()
      this.restoreZoomAnchor()
    },
    changeZoom(delta) { this.zoomAnchor = this.captureZoomAnchor(); this.zoom = Math.max(60, Math.min(180, this.zoom + delta)) },
    fitWidth() { this.zoomAnchor = this.captureZoomAnchor(); this.zoom = 100 },
    scheduleProgressSave() {
      if (!this.book) return
      clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(this.saveProgress, 1200)
    },
    async saveProgress() {
      if (!this.book || !this.totalPages) return
      const payload = {
        bookId: this.book.id, chapterId: null, pageIndex: Math.max(0, this.currentPage - 1),
        totalPages: this.totalPages, pageOffset: this.currentPageOffset(), mediaType: 'PDF', readingMode: 'pdf'
      }
      if (!this.$store.state.token) {
        localStorage.setItem(`nr-pdf-progress:${this.book.id}`, JSON.stringify({ pageIndex: payload.pageIndex, pageOffset: payload.pageOffset }))
        return
      }
      try {
        await api.progress.saveReadingProgress(payload)
      } catch (error) { /* progress failures should not close the document */ }
    },
    marksForPage(pageNo) { return this.bookmarks.filter(item => Number(item.pageIndex) === pageNo - 1 && (item.offsetX != null || item.note)) },
    markStyle(mark) { return { left: `${Math.max(0, Math.min(1, Number(mark.offsetX) || 0.94)) * 100}%`, top: `${Math.max(0, Math.min(1, Number(mark.offsetY) || 0.08)) * 100}%` } },
    async addBookmark(note) {
      if (!this.$store.state.token) return this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } })
      const selected = note || ''
      try {
        const payload = {
          bookId: this.bookId, mediaType: 'PDF', pageIndex: Math.max(0, this.currentPage - 1),
          chapterTitle: `第 ${this.currentPage} 页`, note: selected || null,
          noteType: selected ? 'ANNOTATION' : 'BOOKMARK',
          offsetX: selected && this.selectionOffset ? this.selectionOffset.x : 0.94,
          offsetY: selected && this.selectionOffset ? this.selectionOffset.y : 0.08
        }
        const { data } = await api.bookmark.add(payload)
        this.bookmarks.unshift(data)
        this.clearSelection()
        this.$message.success(selected ? 'PDF 批注已保存' : '页书签已保存')
      } catch (error) { this.$message.error('保存失败，请重试') }
    },
    captureSelection(event) {
      const selection = window.getSelection()
      const text = selection && selection.toString().trim()
      if (!text || !selection.rangeCount) { this.selectionText = ''; return }
      const range = selection.getRangeAt(0)
      const pageNode = event.target.closest && event.target.closest('.pdf-page')
      if (!pageNode) return
      const rect = range.getBoundingClientRect()
      const pageRect = pageNode.getBoundingClientRect()
      this.selectionText = text
      this.selectionPage = Number(pageNode.dataset.page)
      this.currentPage = this.selectionPage
      this.selectionOffset = {
        x: Math.max(0, Math.min(1, (rect.left - pageRect.left) / pageRect.width)),
        y: Math.max(0, Math.min(1, (rect.top - pageRect.top) / pageRect.height))
      }
      this.selectionStyle = { left: `${Math.max(10, Math.min(window.innerWidth - 260, event.clientX - 50))}px`, top: `${Math.max(64, event.clientY - 56)}px` }
    },
    clearSelection() { this.selectionText = ''; this.selectionOffset = null; window.getSelection()?.removeAllRanges() },
    showBookmark(item) { this.activeBookmark = item; setTimeout(() => { this.activeBookmark = null }, 4500) },
    async searchText() {
      if (!this.pdf || !this.searchQuery || this.searching) return
      const token = ++this.searchToken
      const query = this.searchQuery.toLocaleLowerCase()
      this.searching = true; this.searchMessage = '正在搜索…'; this.searchPages = []
      const matches = []
      let pagesWithText = 0
      try {
        for (let pageNo = 1; pageNo <= this.totalPages; pageNo++) {
          if (token !== this.searchToken) return
          const page = await this.pdf.getPage(pageNo)
          const content = await page.getTextContent()
          const text = content.items.map(item => item.str || '').join(' ').toLocaleLowerCase()
          if (text.trim()) pagesWithText++
          if (text.includes(query)) matches.push(pageNo)
        }
        this.searchPages = matches
        this.searchCursor = 0
        if (!matches.length) this.searchMessage = pagesWithText ? '没有找到匹配文字' : '此 PDF 没有可搜索的文字层'
        else {
          this.searchMessage = `1 / ${matches.length}`
          await this.goToPage(matches[0])
        }
      } catch (error) { this.searchMessage = '搜索失败，请重试' }
      finally { if (token === this.searchToken) this.searching = false }
    },
    stepSearch(direction) {
      if (!this.searchPages.length) return
      this.searchCursor = (this.searchCursor + direction + this.searchPages.length) % this.searchPages.length
      this.searchMessage = `${this.searchCursor + 1} / ${this.searchPages.length}`
      this.goToPage(this.searchPages[this.searchCursor])
    },
    goBack() { if (window.history.length > 1) this.$router.back(); else this.$router.push('/library') }
  }
}
</script>

<style lang="scss" scoped>
.pdf-reader { position: fixed; inset: 0; display: flex; flex-direction: column; overflow: hidden; color: var(--nr-text); background: var(--nr-bg); }
.pdf-header { z-index: 5; display: flex; height: 62px; flex: 0 0 62px; align-items: center; gap: 12px; padding: 0 20px; border-bottom: 1px solid var(--nr-border); background: var(--nr-surface); }
.icon-button { display: grid; width: 38px; height: 38px; flex: 0 0 38px; place-items: center; color: var(--nr-text-2); border: 0; border-radius: 9px; background: transparent; cursor: pointer; &:hover { color: var(--nr-accent); background: var(--nr-surface-2); } }
.pdf-title { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 3px; strong { overflow: hidden; font-size: 14px; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; } span { color: var(--nr-text-3); font-size: 11px; } }
.header-tools { display: flex; gap: 3px; }
.pdf-workspace { display: flex; min-height: 0; flex: 1; }
.outline-panel { z-index: 2; display: flex; width: 290px; flex: 0 0 290px; flex-direction: column; overflow-y: auto; border-right: 1px solid var(--nr-border); background: var(--nr-surface); }
.outline-heading { position: sticky; top: 0; z-index: 1; display: flex; min-height: 48px; align-items: center; justify-content: space-between; padding: 0 14px; border-bottom: 1px solid var(--nr-border); background: var(--nr-surface); font-size: 13px; button { display: grid; width: 32px; height: 32px; place-items: center; color: var(--nr-text-3); border: 0; background: transparent; cursor: pointer; } }
.outline-list { padding: 8px; button { display: block; width: 100%; min-height: 35px; overflow: hidden; padding-right: 10px; color: var(--nr-text-2); border: 0; border-radius: 6px; background: transparent; font-size: 12px; text-align: left; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; &:hover { color: var(--nr-accent); background: var(--nr-surface-2); } } }
.outline-empty { padding: 18px 14px; color: var(--nr-text-3); font-size: 12px; }
.outline-list,.thumbnail-list { min-height: 0; flex: 1; overflow-y: auto; }
.thumbnail-spacer { position: relative; width: 100%; }
.thumbnail-item { position: absolute; top: 0; left: 0; display: flex; width: 100%; height: 154px; flex-direction: column; align-items: center; justify-content: center; gap: 5px; color: var(--nr-text-3); border: 2px solid transparent; background: transparent; font-size: 10px; cursor: pointer; &:hover { background: var(--nr-surface-2); } &.current { color: var(--nr-accent); border-color: var(--nr-accent); background: var(--nr-accent-bg); } canvas { display: block; max-width: 100%; max-height: 126px; background: #fff; box-shadow: 0 1px 5px rgba(20,30,23,.16); } }
.saved-pages { display: flex; flex-direction: column; gap: 4px; padding: 10px 8px 16px; border-top: 1px solid var(--nr-border); strong { padding: 4px 7px 7px; font-size: 12px; } button { display: flex; flex-direction: column; gap: 3px; padding: 8px; color: var(--nr-text-2); border: 0; border-radius: 6px; background: transparent; text-align: left; cursor: pointer; &:hover { background: var(--nr-surface-2); } small { overflow: hidden; color: var(--nr-text-3); text-overflow: ellipsis; white-space: nowrap; } } }
.pdf-main { position: relative; min-width: 0; flex: 1; }
.pdf-scroll { position: absolute; inset: 0; overflow: auto; overscroll-behavior: contain; scrollbar-gutter: stable; }
.page-flow { display: flex; min-width: 0; flex-direction: column; align-items: center; padding: 22px 14px 34px; }
.page-spacer { width: 1px; flex: 0 0 auto; }
.pdf-page { position: relative; flex: 0 0 auto; max-width: 100%; margin-bottom: 28px; background: #fff; box-shadow: 0 3px 18px rgba(24,31,26,.17); }
.pdf-page-body { position: relative; width: 100%; line-height: 0; }
.pdf-canvas { display: block; max-width: 100%; height: auto; background: #fff; }
.pdf-text-layer { position: absolute; inset: 0; z-index: 1; overflow: hidden; line-height: 1; opacity: .2; user-select: text; -webkit-user-select: text; ::v-deep span { position: absolute; color: transparent; white-space: pre; transform-origin: 0 0; cursor: text; } }
.page-caption { position: absolute; right: 0; bottom: -22px; left: 0; color: var(--nr-text-3); font-size: 10px; line-height: 1; text-align: center; }
.page-mark { position: absolute; z-index: 2; display: grid; width: 22px; height: 22px; place-items: center; color: #fff; border: 2px solid #fff; border-radius: 50%; background: var(--nr-accent); box-shadow: 0 2px 7px rgba(0,0,0,.24); font-size: 15px; line-height: 1; cursor: pointer; transform: translate(-50%, -50%); }
.pdf-state { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 13px; color: var(--nr-text-3); font-size: 14px; &.error-state { padding: 28px; color: var(--nr-danger); text-align: center; strong { color: var(--nr-text); font-size: 16px; } p { max-width: 440px; color: var(--nr-text-2); font-size: 13px; line-height: 1.6; } } }
.pdf-toolbar { z-index: 4; display: flex; min-height: 58px; align-items: center; justify-content: space-between; gap: 18px; padding: 8px 20px; border-top: 1px solid var(--nr-border); background: var(--nr-surface); }
.page-jump,.zoom-tools { display: flex; align-items: center; gap: 8px; color: var(--nr-text-2); font-size: 12px; white-space: nowrap; }
.page-jump input { width: 54px; height: 32px; padding: 0 6px; color: var(--nr-text); border: 1px solid var(--nr-border); border-radius: 7px; background: var(--nr-bg); text-align: center; }
.zoom-tools button,.toolbar-search button { display: grid; width: 32px; height: 32px; place-items: center; color: var(--nr-text-2); border: 1px solid var(--nr-border); border-radius: 7px; background: var(--nr-surface); cursor: pointer; &:disabled { opacity: .4; cursor: default; } &:hover:not(:disabled) { color: var(--nr-accent); border-color: var(--nr-accent); } }
.zoom-tools .fit-button { width: auto; padding: 0 9px; font-size: 11px; }
.toolbar-search { position: relative; display: flex; min-width: 150px; flex: 1; justify-content: center; gap: 4px; input { width: min(240px, 100%); height: 32px; padding: 0 10px; color: var(--nr-text); border: 1px solid var(--nr-border); border-radius: 7px; background: var(--nr-bg); font-size: 12px; } .search-step { width: 27px; height: 32px; } }
.search-message { position: absolute; top: -20px; color: var(--nr-text-3); font-size: 10px; white-space: nowrap; }
.selection-popover { position: fixed; z-index: 20; display: flex; max-width: calc(100vw - 20px); align-items: center; gap: 8px; padding: 7px 8px 7px 10px; border: 1px solid var(--nr-border); border-radius: 9px; background: var(--nr-surface); box-shadow: var(--nr-shadow-float); transform: translateX(-50%); span { max-width: 180px; overflow: hidden; color: var(--nr-text-2); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; } button { display: flex; min-height: 30px; align-items: center; gap: 5px; padding: 0 8px; color: var(--nr-accent); border: 0; border-radius: 6px; background: var(--nr-accent-bg); font-size: 11px; white-space: nowrap; cursor: pointer; } .dismiss-selection { width: 25px; justify-content: center; padding: 0; color: var(--nr-text-3); background: transparent; } }
.bookmark-toast { position: fixed; right: 22px; bottom: 76px; z-index: 20; display: flex; max-width: min(420px, calc(100vw - 32px)); align-items: center; gap: 10px; padding: 11px 12px; border: 1px solid var(--nr-border); border-radius: 10px; background: var(--nr-surface); box-shadow: var(--nr-shadow-float); cursor: pointer; strong { flex: 0 0 auto; font-size: 12px; } span { overflow: hidden; color: var(--nr-text-2); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; } button { display: grid; width: 24px; height: 24px; flex: 0 0 24px; place-items: center; color: var(--nr-text-3); border: 0; background: transparent; } }
@media (max-width: 760px) {
  .pdf-header { height: 56px; flex-basis: 56px; gap: 7px; padding: 0 8px; }
  .icon-button { width: 35px; height: 35px; flex-basis: 35px; }
  .pdf-title strong { font-size: 13px; }
  .outline-panel { position: absolute; inset: 56px 0 57px; z-index: 4; width: auto; border-right: 0; box-shadow: var(--nr-shadow-float); }
  .page-flow { padding: 10px 7px 28px; }
  .pdf-page { margin-bottom: 25px; }
  .pdf-toolbar { min-height: 58px; flex-wrap: wrap; justify-content: space-between; gap: 4px 8px; padding: 6px 8px calc(6px + env(safe-area-inset-bottom)); }
  .page-jump { gap: 5px; font-size: 11px; }
  .page-jump input { width: 46px; height: 30px; }
  .toolbar-search { order: 3; min-width: 0; flex: 1 0 100%; justify-content: flex-start; .search-message { top: -14px; right: 0; } }
  .toolbar-search input { flex: 1; }
  .zoom-tools { gap: 4px; }
  .zoom-tools button { width: 29px; height: 29px; }
  .zoom-tools .fit-button { width: auto; }
  .bookmark-toast { right: 10px; bottom: 88px; }
}
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { scroll-behavior: auto !important; transition-duration: .01ms !important; } }
</style>
